/**
 * SVG bar chart for the admin metrics "Usage over time" panel (plan 17.3, which names this
 * exact recipe: "copy verbatim rather than adding a charting dependency"). Copied from the
 * main app's `client/src/pages/AdminTokenUsagePage.js` (lines ~93-242, `niceYMax`/`BarChart`)
 * -- same geometry, same padding/step rules, same hover tooltip. The one deliberate change
 * is the value-formatting seam: the source component special-cased `graphMetric === 'cost'`
 * inline to prefix Y-axis ticks with `$`; this copy takes a `formatValue` callback instead
 * (used for both the Y-axis ticks and the tooltip), since this page's series can be tokens,
 * cost or call-count and the caller already knows which.
 *
 * `data` is `[{ period, periodLabel, value }]` from `metricsApi.getTimeSeries`'s
 * `series[]`. An empty series renders an explicit "no data" message rather than nothing --
 * a silently blank panel reads as broken during a demo, not as "zero for this range".
 *
 * Two departures from the copied geometry, both found on the live site (2026-09-26): the
 * Y axis ticks at 1/2/5 x 10^k steps (the copy ticked at 1,971,993 / 3,943,986 / ...), and the
 * left margin grows with the longest tick label (a fixed 48 px cut "9,859,965" to "59,965").
 * `formatAxis` shortens tick labels ("2M"); `formatValue` still shows the full value on hover.
 */
import React from 'react';

// Roughly the width of one 12 px axis character, for sizing the left margin to its labels.
const AXIS_CHAR_PX = 7;

/** Y-axis ticks from 0 in about five round steps (1, 2 or 5 x 10^k); whole steps for counts. */
export function niceTicks(maxVal, { integer = false } = {}) {
  if (!(maxVal > 0)) return [0, 1];
  const raw = maxVal / 5;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / magnitude;
  let step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * magnitude;
  if (integer) step = Math.max(1, step);
  const decimals = Math.max(0, -Math.floor(Math.log10(step)));
  const intervals = Math.ceil(maxVal / step - 1e-9);
  return Array.from({ length: intervals + 1 }, (_, i) => Number((i * step).toFixed(decimals)));
}

export default function BarChart({
  data,
  metricLabel = 'Value',
  formatValue = (v) => String(v),
  formatAxis,
  integer = false,
}) {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });
  const containerRef = React.useRef(null);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 rounded-lg border border-dashed border-slate-200 text-sm text-slate-500">
        No usage data for this range.
      </div>
    );
  }

  const values = data.map((d) => Number(d.value) || 0);
  const maxVal = Math.max(...values, 0);
  const yTicks = niceTicks(maxVal, { integer });
  const yMax = yTicks[yTicks.length - 1];
  const axisLabel = formatAxis || formatValue;
  const longestTick = Math.max(...yTicks.map((t) => String(axisLabel(t)).length));
  const n = data.length;
  const width = Math.max(640, Math.min(1400, n * 38));
  const padding = { left: Math.max(48, 16 + longestTick * AXIS_CHAR_PX), right: 24, top: 20, bottom: n > 12 ? 56 : 44 };
  const height = 280;
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const barGap = Math.max(2, Math.min(8, plotWidth / n / 4));
  const barWidth = Math.max(4, (plotWidth - barGap * (n - 1)) / n);
  const xScale = (i) => padding.left + i * (barWidth + barGap) + barWidth / 2;
  const yScale = (v) => padding.top + plotHeight - (yMax > 0 ? (v / yMax) * plotHeight : 0);
  const labelRotate = n > 12;

  const handleMouseMove = (e, i) => {
    setHoveredIndex(i);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleMouseLeave = () => setHoveredIndex(null);

  const hovered = hoveredIndex != null ? data[hoveredIndex] : null;

  return (
    <div ref={containerRef} className="w-full overflow-x-auto relative">
      <div style={{ width, minWidth: width }}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMinYMid meet"
          onMouseLeave={handleMouseLeave}
          role="img"
          aria-label={`${metricLabel} over time`}
        >
          {/* Horizontal grid lines */}
          {yTicks.map((t, i) => (
            <line
              key={i}
              x1={padding.left}
              y1={yScale(t)}
              x2={width - padding.right}
              y2={yScale(t)}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
          ))}
          {/* Y-axis labels */}
          {yTicks.map((t, i) => (
            <text
              key={i}
              x={padding.left - 8}
              y={yScale(t) + 4}
              textAnchor="end"
              className="fill-slate-500 text-xs"
              fontSize={12}
              data-testid="y-axis-label"
            >
              {axisLabel(t)}
            </text>
          ))}
          {/* X-axis labels -- rotate when many points to avoid overlap */}
          {data.map((d, i) => {
            let label = d.periodLabel ?? '';
            if (typeof label === 'string' && label.length > 10) {
              label = label.slice(0, 10) + '…';
            }
            const x = xScale(i);
            const y = height - padding.bottom + (labelRotate ? 14 : 18);
            return (
              <text
                key={i}
                x={x}
                y={y}
                textAnchor={labelRotate ? 'end' : 'middle'}
                className="fill-slate-500 text-xs"
                fontSize={11}
                transform={labelRotate ? `rotate(-45 ${x} ${y})` : undefined}
              >
                {label}
              </text>
            );
          })}
          {/* Blue bars with hover */}
          {data.map((d, i) => {
            const val = Number(d.value) || 0;
            const barX = padding.left + i * (barWidth + barGap);
            const barY = yScale(val);
            const barH = plotHeight - (yScale(val) - padding.top);
            const isHovered = hoveredIndex === i;
            return (
              <g key={i} onMouseMove={(e) => handleMouseMove(e, i)} data-testid="bar-chart-bar">
                <title>{`${d.periodLabel}: ${metricLabel} ${formatValue(val)}`}</title>
                <rect
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={Math.max(0, barH)}
                  fill={isHovered ? '#1d4ed8' : '#2563eb'}
                  rx={2}
                  className="transition-colors"
                />
              </g>
            );
          })}
        </svg>
      </div>
      {/* Hover tooltip */}
      {hovered != null && (
        <div
          className="absolute z-10 pointer-events-none rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-left min-w-[140px]"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: 'translate(-50%, -100%) translateY(-8px)',
          }}
        >
          <div className="text-xs font-medium text-slate-500">{hovered.periodLabel}</div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5">
            {metricLabel}: {formatValue(Number(hovered.value) || 0)}
          </div>
        </div>
      )}
      {data.length > 12 && (
        <p className="mt-2 text-xs text-slate-500">Scroll horizontally to see all dates.</p>
      )}
    </div>
  );
}
